import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Transaction, Account } from '../types';
import CategorySelect from '../components/CategorySelect';
import ReceiptScanner from '../components/ReceiptScanner';
import { getCategoryDisplayName, getAccountTypeDisplayName } from '../services/categoryService';
import { Analytics } from '../utils/analytics';

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [formData, setFormData] = useState({
    accountId: '',
    amount: '',
    category: 'EXPENSE',
    customCategory: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  // Filter States
  const [filters, setFilters] = useState({
    searchText: '',
    accountId: '',
    category: '',
    type: '', // 'income', 'expense', or ''
    period: 'all', // 'all', 'thisMonth', 'lastMonth', 'thisYear', 'custom'
    dateFrom: '',
    dateTo: ''
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transRes, accRes, catRes] = await Promise.all([
        api.get('/transactions'),
        api.get('/accounts'),
        api.get('/categories')
      ]);
      setTransactions(transRes.data.data.transactions);
      setAccounts(accRes.data.data.accounts);
      setCategories(catRes.data.data.categories);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  // Hilfsfunktion: Hole Display-Namen für Kategorie
  const getDisplayNameForTransaction = (transaction: Transaction) => {
    const customCat = (transaction as any).customCategory;
    if (customCat) {
      // Suche nach der Custom-Kategorie in der Liste
      const category = categories.find(cat => cat.name === customCat && cat.isCustom);
      return category ? category.displayName : customCat;
    }
    return getCategoryDisplayName(transaction.category);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Bereite Daten vor - sende customCategory nur wenn gesetzt
      const submitData: any = { ...formData };
      if (!formData.customCategory) {
        delete submitData.customCategory;
      }
      
      await api.post('/transactions', submitData);
      Analytics.transactionCreated(formData.category, parseFloat(formData.amount));
      setShowForm(false);
      setFormData({
        accountId: '',
        amount: '',
        category: 'EXPENSE',
        customCategory: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      console.error('Fehler beim Erstellen der Transaktion:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Transaktion wirklich löschen?')) {
      try {
        await api.delete(`/transactions/${id}`);
        Analytics.transactionDeleted();
        fetchData();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  const handleReceiptScan = async (data: { accountId: string; amount: string; category: string; description: string; date: string }) => {
    try {
      await api.post('/transactions', data);
      Analytics.receiptScanned(true);
      Analytics.transactionCreated(data.category, parseFloat(data.amount));
      setShowScanner(false);
      fetchData();
    } catch (error) {
      console.error('Fehler beim Erstellen der Transaktion:', error);
      Analytics.receiptScanned(false);
      alert('Fehler beim Speichern der Transaktion');
    }
  };

  // Filter-Logik
  const getFilteredTransactions = () => {
    return transactions.filter(t => !t.isRecurring).filter(transaction => {
      // Suchtext
      if (filters.searchText && !transaction.description.toLowerCase().includes(filters.searchText.toLowerCase())) {
        return false;
      }
      
      // Konto
      if (filters.accountId && transaction.accountId !== filters.accountId) {
        return false;
      }
      
      // Kategorie
      if (filters.category && transaction.category !== filters.category) {
        return false;
      }
      
      // Typ (Einnahme/Ausgabe)
      const amount = parseFloat(transaction.amount.toString());
      if (filters.type === 'income' && amount < 0) {
        return false;
      }
      if (filters.type === 'expense' && amount >= 0) {
        return false;
      }
      
      // Zeitraum
      const transDate = new Date(transaction.date);
      const now = new Date();
      
      if (filters.period === 'thisMonth') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        if (transDate < startOfMonth || transDate > endOfMonth) {
          return false;
        }
      } else if (filters.period === 'lastMonth') {
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        if (transDate < startOfLastMonth || transDate > endOfLastMonth) {
          return false;
        }
      } else if (filters.period === 'thisYear') {
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        if (transDate < startOfYear) {
          return false;
        }
      } else if (filters.period === 'custom') {
        if (filters.dateFrom && transDate < new Date(filters.dateFrom)) {
          return false;
        }
        if (filters.dateTo && transDate > new Date(filters.dateTo)) {
          return false;
        }
      }
      
      return true;
    });
  };

  const filteredTransactions = getFilteredTransactions();
  
  // Pagination berechnen
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);
  
  // Bei Filter-Änderung zurück auf Seite 1
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);
  
  // Alle verfügbaren Kategorien sammeln
  const allCategories = Array.from(new Set(transactions.map(t => t.category)));

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap',
        marginBottom: '20px' 
      }}>
        <h1 style={{ 
          minWidth: isMobile ? '100%' : 'auto',
          marginBottom: isMobile ? '10px' : '0'
        }}>
          Transaktionen
        </h1>
        <div style={{ 
          display: 'flex', 
          gap: '10px',
          width: isMobile ? '100%' : 'auto'
        }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowScanner(true)}
            style={{
              padding: '8px 16px',
              flex: isMobile ? 1 : 'none'
            }}
          >
            📸 Beleg scannen
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(!showForm)}
            style={{
              padding: '8px 16px',
              flex: isMobile ? 1 : 'none'
            }}
          >
            {showForm ? 'Abbrechen' : '+ Manuell'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card">
          <h2>Neue Transaktion erstellen</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Konto *</label>
              <select
                value={formData.accountId}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                required
              >
                <option value="">Bitte wählen...</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({getAccountTypeDisplayName(acc.type)})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Betrag (€) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="z.B. -50.00 für Ausgaben, 100.00 für Einnahmen"
                required
              />
              <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                Negative Werte für Ausgaben, positive für Einnahmen
              </small>
            </div>

            <CategorySelect
              value={formData.category}
              onChange={(value, isCustom) => setFormData({ 
                ...formData, 
                category: value,
                customCategory: isCustom ? value : ''
              })}
              required
            />

            <div className="form-group">
              <label>Beschreibung *</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="z.B. Einkauf bei Edeka"
                required
              />
            </div>

            <div className="form-group">
              <label>Datum *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-success"
              style={{
                width: isMobile ? '100%' : 'auto',
                padding: '12px 24px',
                marginTop: '10px'
              }}
            >
              Transaktion erstellen
            </button>
          </form>
        </div>
      )}

      {/* Filter-Bereich */}
      <div className="card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <h3 style={{ marginBottom: '20px', fontSize: '18px', color: '#334155' }}>🔍 Filter</h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '15px'
        }}>
          {/* Suchfeld */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Suche</label>
            <input
              type="text"
              placeholder="Beschreibung suchen..."
              value={filters.searchText}
              onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Konto */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Konto</label>
            <select
              value={filters.accountId}
              onChange={(e) => setFilters({ ...filters, accountId: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Alle Konten</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
          </div>

          {/* Kategorie */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Kategorie</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Alle Kategorien</option>
              {allCategories.map(cat => (
                <option key={cat} value={cat}>{getCategoryDisplayName(cat)}</option>
              ))}
            </select>
          </div>

          {/* Typ */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Typ</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Alle</option>
              <option value="income">Nur Einnahmen</option>
              <option value="expense">Nur Ausgaben</option>
            </select>
          </div>

          {/* Zeitraum */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Zeitraum</label>
            <select
              value={filters.period}
              onChange={(e) => setFilters({ ...filters, period: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">Alle</option>
              <option value="thisMonth">Dieser Monat</option>
              <option value="lastMonth">Letzter Monat</option>
              <option value="thisYear">Dieses Jahr</option>
              <option value="custom">Benutzerdefiniert</option>
            </select>
          </div>
        </div>

        {/* Benutzerdefinierter Zeitraum */}
        {filters.period === 'custom' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '15px',
            marginTop: '15px',
            padding: '15px',
            background: 'white',
            borderRadius: '8px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Von</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Bis</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        )}

        {/* Filter zurücksetzen Button */}
        <button
          onClick={() => setFilters({
            searchText: '',
            accountId: '',
            category: '',
            type: '',
            period: 'all',
            dateFrom: '',
            dateTo: ''
          })}
          className="btn btn-secondary"
          style={{ 
            marginTop: '15px',
            fontSize: '13px',
            padding: '6px 12px'
          }}
        >
          Filter zurücksetzen
        </button>
        
        {/* Anzahl der gefilterten Transaktionen */}
        <div style={{ 
          marginTop: '15px', 
          fontSize: '13px', 
          color: '#64748b',
          fontWeight: '500'
        }}>
          {filteredTransactions.length} von {transactions.filter(t => !t.isRecurring).length} Transaktionen
        </div>
      </div>

      <div className="card">
        <h2>Transaktionen</h2>
        {filteredTransactions.length === 0 ? (
          <p style={{ color: '#666', marginTop: '10px' }}>
            {transactions.filter(t => !t.isRecurring).length === 0 
              ? 'Keine Transaktionen vorhanden.' 
              : 'Keine Transaktionen gefunden, die den Filterkriterien entsprechen.'}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Konto</th>
                <th>Kategorie</th>
                <th>Beschreibung</th>
                <th>Betrag</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.map((transaction) => {
                const amount = parseFloat(transaction.amount.toString());
                const isPositive = amount >= 0;
                
                return (
                  <tr key={transaction.id}>
                    <td data-label="Datum">{new Date(transaction.date).toLocaleDateString('de-DE')}</td>
                    <td data-label="Konto">{transaction.account?.name}</td>
                    <td data-label="Kategorie">{getDisplayNameForTransaction(transaction)}</td>
                    <td data-label="Beschreibung">{transaction.description}</td>
                    <td data-label="Betrag" style={{ color: isPositive ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                      {isPositive ? '+' : ''} {amount.toFixed(2)} €
                    </td>
                    <td data-label="Aktionen">
                      <button
                        className="btn btn-danger"
                        style={{ 
                          fontSize: '12px', 
                          padding: '5px 10px',
                          width: isMobile ? '100%' : 'auto'
                        }}
                        onClick={() => handleDelete(transaction.id)}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e2e8f0',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Seite {currentPage} von {totalPages} ({startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)} von {filteredTransactions.length})
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  fontSize: '13px',
                  padding: '6px 12px',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Zurück
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  fontSize: '13px',
                  padding: '6px 12px',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Weiter →
              </button>
            </div>
          </div>
        )}
      </div>

      {transactions.filter(t => t.isRecurring).length > 0 && (
        <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8f9fa', opacity: 0.8 }}>
          <h2 style={{ color: '#666' }}>Vorgemerkte Umsätze (Abos)</h2>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
            Diese werden bei Fälligkeit automatisch gebucht oder können im Dashboard bestätigt werden
          </p>
          <table style={{ marginTop: '10px' }}>
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Konto</th>
                <th>Kategorie</th>
                <th>Betrag</th>
                <th>Buchungstag</th>
                <th>Modus</th>
              </tr>
            </thead>
            <tbody>
              {transactions.filter(t => t.isRecurring).map((transaction) => {
                const amount = parseFloat(transaction.amount.toString());
                
                return (
                  <tr key={transaction.id} style={{ color: '#666' }}>
                    <td data-label="Beschreibung"><strong>{transaction.description}</strong></td>
                    <td data-label="Konto">{transaction.account?.name}</td>
                    <td data-label="Kategorie"><span className="badge badge-info">{getDisplayNameForTransaction(transaction)}</span></td>
                    <td data-label="Betrag" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                      {Math.abs(amount).toFixed(2)} €
                    </td>
                    <td data-label="Buchungstag">Jeden {(transaction as any).bookingDay || new Date(transaction.date).getDate()}.</td>
                    <td data-label="Modus">
                      {(transaction as any).autoBook ? (
                        <span className="badge badge-success">Automatisch</span>
                      ) : (
                        <span className="badge badge-warning">Manuell</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Receipt Scanner Modal */}
      {showScanner && (
        <ReceiptScanner
          accounts={accounts}
          onSave={handleReceiptScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export default Transactions;
